import { useState, useRef, useEffect } from 'react'
import {
  Button,
  Input,
  Textarea,
  Select,
  makeStyles,
  tokens,
  Field,
  Spinner,
  Text,
  Badge,
  Tab,
  TabList,
  SelectTabData,
  SelectTabEvent,
} from '@fluentui/react-components'
import {
  Send24Regular,
  Attach24Regular,
  Dismiss24Regular,
  CheckmarkCircle24Filled,
  Image24Regular,
  Document24Regular,
  Wrench24Regular,
  ChatHelp24Regular,
  Camera24Regular,
  Person24Regular,
} from '@fluentui/react-icons'
import { useConfig } from '../context/ConfigContext'
import { api, type TicketType } from '../lib/api'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    maxWidth: '680px',
    margin: '0 auto',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    color: tokens.colorNeutralForeground2,
    fontSize: '14px',
  },
  tabDescription: {
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
    marginTop: '4px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  screenshotHint: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground4,
    borderRadius: '8px',
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
  },
  screenshotHintIcon: {
    color: tokens.colorBrandForeground1,
    fontSize: '24px',
    flexShrink: 0,
  },
  screenshotHintText: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.4',
  },
  screenshotHintBold: {
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  attachments: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  attachmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  attachmentItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '6px',
    fontSize: '13px',
  },
  attachmentInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    overflow: 'hidden',
  },
  attachmentName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  attachmentSize: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
  success: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '40px 20px',
    textAlign: 'center',
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '48px',
  },
  successTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  successMessage: {
    color: tokens.colorNeutralForeground2,
    fontSize: '14px',
    lineHeight: '1.5',
  },
  ticketNumber: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorBrandForeground1,
  },
  uploadButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  hiddenInput: {
    display: 'none',
  },
  managerSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  managerLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  requiredBadge: {
    fontSize: '11px',
  },
})

interface UploadedFile {
  id: string
  filename: string
  fileSize: number
  contentType: string
}

export function SubmitTicket() {
  const styles = useStyles()
  const { config } = useConfig()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Form mode
  const [ticketType, setTicketType] = useState<TicketType>('support')

  // Common fields
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [priority, setPriority] = useState(config?.integration.defaultPriority || 'medium')
  const [categoryId, setCategoryId] = useState('')
  const [subcategoryId, setSubcategoryId] = useState('')
  const [attachments, setAttachments] = useState<UploadedFile[]>([])

  // IT Request fields
  const [requestItems, setRequestItems] = useState('')
  const [businessJustification, setBusinessJustification] = useState('')
  const [managerEmail, setManagerEmail] = useState('')
  const [managerName, setManagerName] = useState('')

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ ticketNumber: string; message: string } | null>(null)

  useEffect(() => {
    if (config?.integration.defaultPriority) {
      setPriority(config.integration.defaultPriority)
    }
  }, [config])

  // Get subcategories for selected category
  const selectedCategory = config?.categories?.find((c: any) => c.id === categoryId)
  const subcategories = selectedCategory?.subcategories || []

  // Reset subcategory when category changes
  useEffect(() => {
    setSubcategoryId('')
  }, [categoryId])

  const resetForm = () => {
    setSubject('')
    setMessage('')
    setPriority(config?.integration.defaultPriority || 'medium')
    setCategoryId('')
    setSubcategoryId('')
    setAttachments([])
    setRequestItems('')
    setBusinessJustification('')
    setManagerEmail('')
    setManagerName('')
    setError(null)
  }

  const handleTabChange = (_: SelectTabEvent, data: SelectTabData) => {
    setTicketType(data.value as TicketType)
    resetForm()
  }

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim()) {
      setError('Please fill in all required fields')
      return
    }

    if (ticketType === 'it_request' && !managerEmail.trim()) {
      setError('Manager email is required for IT requests')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const result = await api.createTicket({
        subject: subject.trim(),
        message: message.trim(),
        priority,
        categoryId: categoryId || undefined,
        subcategoryId: subcategoryId || undefined,
        attachmentIds: attachments.map(a => a.id),
        ticketType,
        ...(ticketType === 'it_request' ? {
          requestItems: requestItems.trim() || undefined,
          businessJustification: businessJustification.trim() || undefined,
          managerEmail: managerEmail.trim(),
          managerName: managerName.trim() || undefined,
        } : {}),
      })

      setSuccess({
        ticketNumber: result.ticket.ticketNumber,
        message: result.message,
      })
      resetForm()
    } catch (err) {
      console.error('Submit error:', err)
      setError(err instanceof Error ? err.message : 'Failed to submit ticket')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files?.length) return

    setIsUploading(true)
    setError(null)

    try {
      for (const file of Array.from(files)) {
        if (file.size > 25 * 1024 * 1024) {
          setError(`File "${file.name}" exceeds the 25MB limit`)
          continue
        }
        const result = await api.uploadFile(file)
        setAttachments(prev => [...prev, {
          id: result.attachment.id,
          filename: result.attachment.filename,
          fileSize: result.attachment.fileSize,
          contentType: result.attachment.contentType,
        }])
      }
    } catch (err) {
      console.error('Upload error:', err)
      setError(err instanceof Error ? err.message : 'Failed to upload file')
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id))
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (success) {
    return (
      <div className={styles.root}>
        <div className={styles.success}>
          <CheckmarkCircle24Filled className={styles.successIcon} />
          <h2 className={styles.successTitle}>
            {ticketType === 'it_request' ? 'IT Request Submitted!' : 'Ticket Submitted!'}
          </h2>
          <p className={styles.ticketNumber}>{success.ticketNumber}</p>
          <p className={styles.successMessage}>
            {success.message || 'Your request has been submitted. Our team will review it shortly.'}
          </p>
          <Button 
            appearance="primary" 
            onClick={() => setSuccess(null)}
            size="large"
          >
            Submit Another Request
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div>
        <h1 className={styles.title}>Submit a Request</h1>
        <p className={styles.subtitle}>
          {config?.integration.welcomeMessage || 'How can we help you today?'}
        </p>
      </div>

      {/* Request Type Tabs */}
      <TabList
        selectedValue={ticketType}
        onTabSelect={handleTabChange}
        size="large"
      >
        <Tab value="support" icon={<ChatHelp24Regular />}>
          Help Desk Ticket
        </Tab>
        <Tab value="it_request" icon={<Wrench24Regular />}>
          IT Request
        </Tab>
      </TabList>

      <p className={styles.tabDescription}>
        {ticketType === 'support'
          ? 'Report an issue, ask a question, or request help with something.'
          : 'Request new equipment, software, licenses, or other IT resources. Requires manager approval.'}
      </p>

      <div className={styles.form}>
        {/* Subject */}
        <Field label="Subject" required>
          <Input
            placeholder={ticketType === 'support'
              ? 'Brief summary of your issue'
              : 'What are you requesting? (e.g., New laptop, Software license)'}
            value={subject}
            onChange={(_, data) => setSubject(data.value)}
            maxLength={500}
          />
        </Field>

        {/* IT Request: What they want */}
        {ticketType === 'it_request' && (
          <Field label="Items Requested" hint="List what you need (e.g., Dell Latitude 5550, Adobe Creative Suite license)">
            <Textarea
              placeholder="Describe the specific items, software, or resources you are requesting..."
              value={requestItems}
              onChange={(_, data) => setRequestItems(data.value)}
              rows={3}
              resize="vertical"
            />
          </Field>
        )}

        {/* Description / Details */}
        <Field
          label={ticketType === 'support' ? 'Description' : 'Additional Details'}
          required
        >
          <Textarea
            placeholder={ticketType === 'support'
              ? 'Please describe your issue in detail. Include any error messages, steps to reproduce, and what you expected to happen.'
              : 'Provide any additional context, specifications, or requirements for your request.'}
            value={message}
            onChange={(_, data) => setMessage(data.value)}
            rows={ticketType === 'support' ? 6 : 4}
            resize="vertical"
          />
        </Field>

        {/* IT Request: Business Justification */}
        {ticketType === 'it_request' && (
          <Field label="Business Justification" hint="Explain why this request is needed for your work">
            <Textarea
              placeholder="How will this help you do your job? Why is this needed? Include any deadlines or project dependencies..."
              value={businessJustification}
              onChange={(_, data) => setBusinessJustification(data.value)}
              rows={3}
              resize="vertical"
            />
          </Field>
        )}

        {/* Priority & Category Row */}
        <div className={styles.row}>
          {config?.integration.allowPrioritySelection && (
            <Field label="Priority">
              <Select
                value={priority}
                onChange={(_, data) => setPriority(data.value)}
              >
                {config?.priorities?.length > 0 ? (
                  config.priorities.map((p: any) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))
                ) : (
                  < >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </ >
                )}
              </Select>
            </Field>
          )}

          {config?.categories && config.categories.length > 0 && (
            <Field label="Category">
              <Select
                value={categoryId}
                onChange={(_, data) => setCategoryId(data.value)}
              >
                <option value="">Select a category</option>
                {config.categories.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>

        {/* Subcategory (shown if category has subcategories) */}
        {subcategories.length > 0 && (
          <Field label="Subcategory">
            <Select
              value={subcategoryId}
              onChange={(_, data) => setSubcategoryId(data.value)}
            >
              <option value="">Select a subcategory</option>
              {subcategories.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {/* IT Request: Manager Approval */}
        {ticketType === 'it_request' && (
          <div className={styles.managerSection}>
            <div className={styles.managerLabel}>
              <Person24Regular />
              Manager Approval
              <Badge appearance="filled" color="danger" size="small" className={styles.requiredBadge}>
                Required
              </Badge>
            </div>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              Your manager must approve this request. They will be notified when you submit.
            </Text>
            <div className={styles.row}>
              <Field label="Manager Email" required>
                <Input
                  type="email"
                  placeholder="manager@company.com"
                  value={managerEmail}
                  onChange={(_, data) => setManagerEmail(data.value)}
                />
              </Field>
              <Field label="Manager Name">
                <Input
                  placeholder="Manager's full name"
                  value={managerName}
                  onChange={(_, data) => setManagerName(data.value)}
                />
              </Field>
            </div>
          </div>
        )}

        {/* Screenshot Encouragement */}
        {attachments.filter(a => a.contentType.startsWith('image/')).length === 0 && (
          <div
            className={styles.screenshotHint}
            onClick={() => fileInputRef.current?.click()}
            style={{ cursor: 'pointer' }}
          >
            <Camera24Regular className={styles.screenshotHintIcon} />
            <div>
              <span className={styles.screenshotHintBold}>
                {ticketType === 'support'
                  ? 'A screenshot helps us solve your issue faster!'
                  : 'Have a reference image or spec sheet?'}
              </span>
              <div className={styles.screenshotHintText}>
                {ticketType === 'support'
                  ? 'Click here to attach a screenshot of the error or issue. This can significantly speed up resolution time.'
                  : 'Click here to attach any relevant images, quotes, or documentation for your request.'}
              </div>
            </div>
          </div>
        )}

        {/* File Attachments */}
        <div className={styles.attachments}>
          <Field label="Attachments (optional)">
            <div className={styles.uploadButton}>
              <Button
                icon={<Attach24Regular />}
                appearance="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Attach Files'}
              </Button>
              {isUploading && <Spinner size="tiny" />}
            </div>
          </Field>
          
          <input
            ref={fileInputRef}
            type="file"
            className={styles.hiddenInput}
            onChange={handleFileUpload}
            multiple
            accept={config?.fileUpload?.allowedTypes?.join(',') || '*'}
          />

          {attachments.length > 0 && (
            <div className={styles.attachmentList}>
              {attachments.map(att => (
                <div key={att.id} className={styles.attachmentItem}>
                  <div className={styles.attachmentInfo}>
                    {att.contentType.startsWith('image/') ? (
                      <Image24Regular />
                    ) : (
                      <Document24Regular />
                    )}
                    <span className={styles.attachmentName}>{att.filename}</span>
                    <span className={styles.attachmentSize}>
                      {formatFileSize(att.fileSize)}
                    </span>
                  </div>
                  <Button
                    appearance="subtle"
                    icon={<Dismiss24Regular />}
                    size="small"
                    onClick={() => removeAttachment(att.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Text>
        )}

        <Button
          appearance="primary"
          icon={<Send24Regular />}
          size="large"
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            !subject.trim() ||
            !message.trim() ||
            (ticketType === 'it_request' && !managerEmail.trim())
          }
        >
          {isSubmitting
            ? 'Submitting...'
            : ticketType === 'it_request'
              ? 'Submit IT Request'
              : 'Submit Ticket'}
        </Button>
      </div>
    </div>
  )
}
